// iot-admin/client/src/pages/Vehicles.js
//
// Cars as a top-level section — the vehicles, not the tablets bolted into them.
//
// ── WHY THIS SCREEN EXISTS ──────────────────────────────────────────────────
// Until V250 there was no car. A car was the device's NAME with a plate typed
// into it by convention, plus a photograph of that plate. The convention was
// already broken in production — six of thirteen in-cab devices were named like
// a registration and the rest were "Lenovo K11", "Sunrise Tab" — and one of the
// unnamed ones had a driver assigned to it. We had a driver driving a tablet
// model.
//
// The question this screen exists to answer is the compliance one:
//
//   what lapses this month?        -> compliance filter (expiring / expired)
//   which cabs have no papers?     -> compliance=missing
//   which cars have no screen?     -> status=unfitted
//
// A commercial cab needs RC, insurance, PUC, fitness and permit. We stored a
// picture of its number plate. An expiry nobody can query is an expiry nobody
// notices, which is the whole argument for the table behind this page.
//
// ── WHAT THIS SCREEN CAN AND CANNOT DO ──────────────────────────────────────
// Create, yes — a cab exists whether or not we have put a tablet in it, and
// forcing the car into existence through a device page is exactly how the plate
// ended up in a free-text name field. Fit a screen, no: which car a tablet sits
// in is a fact about the tablet's placement and belongs on the device page.

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { vehicleService } from '../services/api';
import '../components/DeviceSimTaxiDriver.css'; // std-input / std-muted / std-form-actions

const FUEL_TYPES = ['petrol', 'diesel', 'cng', 'lpg', 'electric', 'hybrid'];

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

// The compliance chip. `state` comes from the server so this screen and the
// detail screen cannot drift into different opinions about what "expiring"
// means — that threshold is one constant, on the server.
const DOC_CHIP_CLASS = {
  ok: 'status-active',
  expiring: 'status-pending',
  expired: 'status-error',
  undated: 'status-pending',
  missing: 'status-disabled',
};

const Vehicles = () => {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, totalCount: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ search: '', status: 'all', compliance: 'all' });
  const [sorting, setSorting] = useState({ sortBy: 'registration', sortOrder: 'asc' });

  const [creating, setCreating] = useState(false);
  const [newVehicle, setNewVehicle] = useState({ registration: '', make: '', model: '', modelYear: '', colour: '', fuelType: '' });
  const [createError, setCreateError] = useState(null);
  // When create is refused because we already hold that plate, we keep the
  // existing car's id so the admin is offered it rather than told "no".
  const [duplicateId, setDuplicateId] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchVehicles = useCallback(async () => {
    try {
      setLoading(true);
      const res = await vehicleService.list({
        page: pagination.page,
        pageSize: pagination.pageSize,
        search: filters.search,
        status: filters.status,
        compliance: filters.compliance,
        sortBy: sorting.sortBy,
        sortOrder: sorting.sortOrder,
      });
      setVehicles(res.data || []);
      if (res.pagination) setPagination((p) => ({ ...p, ...res.pagination }));
      setError(null);
    } catch (err) {
      console.error('Error fetching vehicles:', err);
      setError('Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, filters.search, filters.status, filters.compliance, sorting.sortBy, sorting.sortOrder]);

  useEffect(() => { fetchVehicles(); }, [fetchVehicles]);

  const setFilter = (patch) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPagination((p) => ({ ...p, page: 1 }));
  };

  const handleSort = (field) => {
    setSorting((prev) => ({
      sortBy: field,
      sortOrder: prev.sortBy === field && prev.sortOrder === 'asc' ? 'desc' : 'asc',
    }));
  };
  const sortIcon = (f) => (sorting.sortBy !== f ? '↕' : sorting.sortOrder === 'asc' ? '↑' : '↓');

  const closeCreate = () => {
    setCreating(false);
    setNewVehicle({ registration: '', make: '', model: '', modelYear: '', colour: '', fuelType: '' });
    setCreateError(null);
    setDuplicateId(null);
  };

  const submitCreate = async () => {
    if (newVehicle.registration.trim().length < 4) {
      setCreateError('Registration number must be at least 4 characters'); return;
    }
    setSaving(true);
    setCreateError(null);
    setDuplicateId(null);
    try {
      const res = await vehicleService.create({
        registration: newVehicle.registration.trim(),
        make: newVehicle.make.trim() || null,
        model: newVehicle.model.trim() || null,
        modelYear: newVehicle.modelYear ? Number(newVehicle.modelYear) : null,
        colour: newVehicle.colour.trim() || null,
        fuelType: newVehicle.fuelType || null,
      });
      // Straight to the car — the next thing to do is upload its papers.
      navigate(`/vehicles/${res.data.id}`);
    } catch (e) {
      setCreateError(e.response?.data?.error || 'Failed to create vehicle');
      setDuplicateId(e.response?.data?.data?.existingVehicleId || null);
      setSaving(false);
    }
  };

  // Per-document, never one boolean: "has some papers" is not a compliance
  // answer when the missing one is the insurance.
  const docChips = (v) => (
    <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {['rc', 'insurance', 'puc', 'fitness', 'permit'].map((kind) => {
        const d = v.documents?.[kind];
        if (!d) return null;
        const mark = d.state === 'ok' ? '✓' : d.state === 'expired' ? '✕' : d.state === 'missing' ? '○' : '!';
        const title = d.state === 'missing' ? `${d.label} not on file`
          : d.state === 'expired' ? `${d.label} expired ${Math.abs(d.daysLeft)} days ago`
          : d.state === 'expiring' ? `${d.label} expires in ${d.daysLeft} days`
          : d.state === 'undated' ? `${d.label} on file but no expiry recorded`
          : `${d.label} valid until ${fmtDate(d.expiresOn)}`;
        return (
          <span key={kind} className={`status-badge ${DOC_CHIP_CLASS[d.state] || ''}`}
            title={title} style={{ fontSize: '0.7rem' }}>
            {mark} {d.label}
          </span>
        );
      })}
    </span>
  );

  return (
    <div className="users-page">
      <div className="page-header">
        <h1>🚗 Vehicles</h1>
        <button className="btn btn-primary" onClick={() => setCreating(true)}>+ New vehicle</button>
      </div>

      {error && <div className="alert alert-error">❌ {error}</div>}

      {creating && (
        <div className="detail-section">
          <div className="section-header"><h2>New vehicle</h2></div>
          <p className="std-muted">
            Creates the car only. Put a screen in it from that device&rsquo;s page — a
            vehicle can exist here with no tablet fitted.
          </p>
          {createError && (
            <div className="alert alert-error">
              ❌ {createError}
              {duplicateId && (
                <> <Link to={`/vehicles/${duplicateId}`} className="link-button">Open that vehicle →</Link></>
              )}
            </div>
          )}
          <div className="detail-grid">
            <div className="detail-item">
              <label>Registration number</label>
              <input className="std-input" value={newVehicle.registration} autoFocus placeholder="MH 03 EG 6307"
                onChange={(e) => setNewVehicle((v) => ({ ...v, registration: e.target.value }))} />
              <small className="std-muted">
                Spacing and dashes are up to you — they are stripped for matching, so
                this cannot become a second record of a car we already hold.
              </small>
            </div>
            <div className="detail-item">
              <label>Make</label>
              <input className="std-input" value={newVehicle.make} placeholder="Maruti"
                onChange={(e) => setNewVehicle((v) => ({ ...v, make: e.target.value }))} />
            </div>
            <div className="detail-item">
              <label>Model</label>
              <input className="std-input" value={newVehicle.model} placeholder="Dzire"
                onChange={(e) => setNewVehicle((v) => ({ ...v, model: e.target.value }))} />
            </div>
            <div className="detail-item">
              <label>Year</label>
              <input className="std-input" type="number" value={newVehicle.modelYear} placeholder="2022"
                onChange={(e) => setNewVehicle((v) => ({ ...v, modelYear: e.target.value }))} />
            </div>
            <div className="detail-item">
              <label>Colour</label>
              <input className="std-input" value={newVehicle.colour} placeholder="White"
                onChange={(e) => setNewVehicle((v) => ({ ...v, colour: e.target.value }))} />
            </div>
            <div className="detail-item">
              <label>Fuel</label>
              <select className="std-input" value={newVehicle.fuelType}
                onChange={(e) => setNewVehicle((v) => ({ ...v, fuelType: e.target.value }))}>
                <option value="">—</option>
                {FUEL_TYPES.map((f) => <option key={f} value={f}>{f.toUpperCase()}</option>)}
              </select>
            </div>
            <div className="detail-item std-form-actions">
              <button className="btn btn-primary btn-sm" onClick={submitCreate} disabled={saving}>
                {saving ? 'Creating…' : '💾 Create vehicle'}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={closeCreate} disabled={saving}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="filters-bar">
        <div className="search-box">
          <input
            type="text"
            placeholder="🔍 Search registration, make or model…"
            value={filters.search}
            onChange={(e) => setFilter({ search: e.target.value })}
          />
        </div>
        <select value={filters.status} onChange={(e) => setFilter({ status: e.target.value })} title="Is a screen fitted?">
          <option value="all">All vehicles</option>
          <option value="fitted">📺 Screen fitted</option>
          <option value="unfitted">No screen</option>
        </select>
        {/* expiring and expired deliberately OVERLAP — a car can have a lapsed
            PUC and insurance due next week, and both need doing. */}
        <select value={filters.compliance} onChange={(e) => setFilter({ compliance: e.target.value })} title="Document status">
          <option value="all">All documents</option>
          <option value="expired">✕ Something expired</option>
          <option value="expiring">! Expiring within 30 days</option>
          <option value="missing">○ Papers missing</option>
          <option value="complete">✓ Complete and current</option>
        </select>
      </div>

      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : (
        <>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th className="sortable" onClick={() => handleSort('registration')}>Vehicle {sortIcon('registration')}</th>
                  <th>Particulars</th>
                  <th className="sortable" onClick={() => handleSort('devices')}>Screen fitted {sortIcon('devices')}</th>
                  <th>Driver</th>
                  <th>Documents</th>
                  <th className="sortable" onClick={() => handleSort('soonestExpiry')}>Next to lapse {sortIcon('soonestExpiry')}</th>
                </tr>
              </thead>
              <tbody>
                {vehicles.length === 0 ? (
                  <tr><td colSpan="6" className="empty-state">No vehicles match these filters.</td></tr>
                ) : (
                  vehicles.map((v) => (
                    <tr key={v.id}>
                      <td className="user-cell">
                        <div className="user-avatar">🚗</div>
                        <div className="user-info">
                          <Link to={`/vehicles/${v.id}`} className="user-name" style={{ color: '#1976d2', textDecoration: 'none' }}>
                            {v.registration}
                          </Link>
                        </div>
                      </td>
                      <td className="std-muted" style={{ fontSize: '0.85rem' }}>
                        {[v.make, v.model, v.modelYear, v.colour, v.fuelType?.toUpperCase()].filter(Boolean).join(' · ') || '—'}
                      </td>
                      <td>
                        {v.activeDevices > 0 ? (
                          <span className="status-badge status-active">{v.fittedDevices}</span>
                        ) : (
                          <span className="status-badge">None</span>
                        )}
                      </td>
                      <td>{v.currentDriver || <span className="std-muted">—</span>}</td>
                      <td>{docChips(v)}</td>
                      <td>{fmtDate(v.soonestExpiry)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button className="btn btn-secondary btn-sm" disabled={pagination.page <= 1}
                onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}>← Prev</button>
              <span style={{ margin: '0 12px' }}>
                Page {pagination.page} of {pagination.totalPages} · {pagination.totalCount} vehicles
              </span>
              <button className="btn btn-secondary btn-sm" disabled={pagination.page >= pagination.totalPages}
                onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}>Next →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Vehicles;
